import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Calendar, TrendingUp, AlertCircle, Target, Layers, Focus, Edit2, Save, X, CalendarDays } from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ClientOverview({ clientId, clientName, clientData, onNavigate }) {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [editingSession, setEditingSession] = useState(false);
  const [nextSessionDate, setNextSessionDate] = useState('');
  const [sessionFrequency, setSessionFrequency] = useState('weekly');
  const [customIntervalDays, setCustomIntervalDays] = useState(7);
  const dateInputRef = useRef(null);

  useEffect(() => {
    if (clientId && clientData) {
      generateOverview();
    }
  }, [clientId, clientData]);

  const generateOverview = () => {
    if (!clientData) return;

    // Get last session date
    const lastSession = clientData.sessionNotes?.[0];
    const lastSessionDate = lastSession?.session_date ||
      (lastSession?.created_at ? new Date(lastSession.created_at).toLocaleDateString() : 'No sessions yet');

    // Get next session date and frequency from client data or calculate default
    let nextSessionDate = 'Not scheduled';
    let daysUntilNext = null;
    let frequency = 'weekly';

    if (clientData.client?.next_session_date) {
      // Use stored next session date
      nextSessionDate = clientData.client.next_session_date; // Already in YYYY-MM-DD format
      frequency = clientData.client.session_frequency || 'weekly';

      // Calculate days until next session
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextDate = new Date(clientData.client.next_session_date);
      nextDate.setHours(0, 0, 0, 0);
      const diffTime = nextDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      daysUntilNext = diffDays;
    } else if (lastSession?.session_date) {
      // Calculate based on last session and frequency
      const lastDate = new Date(lastSession.session_date);
      const nextDate = new Date(lastDate);
      const daysToAdd = frequency === 'biweekly' ? 14 : frequency === 'monthly' ? 30 : 7;
      nextDate.setDate(nextDate.getDate() + daysToAdd);

      // Calculate days until next session
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      nextDate.setHours(0, 0, 0, 0);
      const diffTime = nextDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      daysUntilNext = diffDays;
      // Format as YYYY-MM-DD
      nextSessionDate = nextDate.toISOString().split('T')[0];
    }

    // Get active objectives count
    const activeObjectives = clientData.objectives?.filter(obj => obj.status !== 'completed').length || 0;
    const completedObjectives = clientData.objectives?.filter(obj => obj.status === 'completed').length || 0;

    // Get latest insights
    const latestInsight = clientData.coachingInsights?.[0];

    // Extract recurring themes from all insights
    const extractThemes = () => {
      if (!clientData.coachingInsights || clientData.coachingInsights.length === 0) {
        return { count: 0, topThemes: ['Gathering insights...'] };
      }

      const allThemes = [];

      // Collect themes from stuck patterns, breakthroughs, and focus areas
      clientData.coachingInsights.forEach(insight => {
        // Extract from stuck patterns
        if (insight.stuck_patterns) {
          const patterns = (insight.stuck_patterns?.split('\n') || []).filter(p => p.trim().length > 0);
          patterns.forEach(p => {
            // Remove bullet points, numbers, and extra formatting
            const cleaned = p.replace(/^[-•*\d.)\]]+\s*/, '').trim();
            if (cleaned.length > 10) allThemes.push(cleaned);
          });
        }

        // Extract from focus areas
        if (insight.focus_areas) {
          const focuses = (insight.focus_areas?.split('\n') || []).filter(f => f.trim().length > 0);
          focuses.forEach(f => {
            const cleaned = f.replace(/^[-•*\d.)\]]+\s*/, '').trim();
            if (cleaned.length > 10) allThemes.push(cleaned);
          });
        }
      });

      // Extract key phrases (3 words or less) from themes
      const extractKeyPhrase = (theme) => {
        // Common patterns to extract meaningful short phrases
        let lowerTheme = theme.toLowerCase();

        // Remove common coaching prefixes to get to the core theme
        const prefixes = [
          'difficulty with', 'difficulty setting', 'difficulty in',
          'struggles with', 'struggling with', 'struggle with',
          'working on', 'focusing on', 'focus on',
          'issues with', 'issue with',
          'problems with', 'problem with',
          'challenges with', 'challenge with',
          'exploring', 'developing', 'improving', 'building', 'practicing',
          'need to work on', 'needs to', 'trying to', 'learning to'
        ];

        // Remove prefixes to get core theme
        for (const prefix of prefixes) {
          if (lowerTheme.startsWith(prefix)) {
            lowerTheme = lowerTheme.substring(prefix.length).trim();
            break;
          }
        }

        // Take first 3 words of the core theme
        const words = lowerTheme.split(' ').slice(0, 3);
        return words.join(' ').trim();
      };

      // Find recurring themes with smart deduplication
      const themeCounts = {};
      const themeMapping = {}; // Maps normalized -> best original phrase

      allThemes.forEach(theme => {
        const keyPhrase = extractKeyPhrase(theme);
        const normalized = keyPhrase.toLowerCase()
          .replace(/[.,;:!?]/g, '')
          .replace(/\s+and\s+/g, ' ') // Remove "and" with spaces
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();

        if (normalized.length === 0) return;

        // Check if this theme is similar to any existing theme
        let matchedKey = null;
        for (const existingKey of Object.keys(themeCounts)) {
          const words1 = normalized.split(' ');
          const words2 = existingKey.split(' ');

          // Check if one is a subset of the other (e.g., "self-sabotage" vs "self-sabotage guilt")
          const isSubset = words1.every(w => words2.includes(w)) || words2.every(w => words1.includes(w));

          if (isSubset) {
            matchedKey = existingKey;
            break;
          }
        }

        if (matchedKey) {
          // Merge with existing theme
          themeCounts[matchedKey] = themeCounts[matchedKey] + 1;

          // Keep the shortest/cleanest version
          if (keyPhrase.length < themeMapping[matchedKey].length) {
            themeMapping[matchedKey] = keyPhrase;
          }
        } else {
          // New unique theme
          themeCounts[normalized] = 1;
          themeMapping[normalized] = keyPhrase;
        }
      });

      // Get themes that appear at least twice, sorted by frequency
      const recurringThemes = Object.entries(themeCounts)
        .filter(([_, count]) => count >= 2)
        .sort((a, b) => b[1] - a[1])
        .map(([normalizedTheme]) => {
          // Use the best/shortest version of this theme
          const bestVersion = themeMapping[normalizedTheme];
          // Capitalize first letter
          return bestVersion.charAt(0).toUpperCase() + bestVersion.slice(1);
        })
        .slice(0, 3);

      // If no recurring themes, extract unique key phrases from recent themes
      if (recurringThemes.length === 0) {
        const seen = new Set();
        const recentThemes = [];

        for (const theme of allThemes) {
          if (recentThemes.length >= 3) break;

          const phrase = extractKeyPhrase(theme);
          const normalized = phrase.toLowerCase().replace(/[.,;:!?]/g, '').replace(/\band\b/g, '').trim();

          // Only add if we haven't seen a similar theme
          if (!seen.has(normalized)) {
            seen.add(normalized);
            recentThemes.push(phrase.charAt(0).toUpperCase() + phrase.slice(1));
          }
        }

        return {
          count: recentThemes.length,
          topThemes: recentThemes.length > 0 ? recentThemes : ['Gathering patterns...']
        };
      }

      return {
        count: recurringThemes.length,
        topThemes: recurringThemes
      };
    };

    // Extract current focus areas from latest insight
    const extractCurrentFocusAreas = () => {
      if (!latestInsight?.focus_areas) {
        return { count: 0, primaryFocus: 'Upload a session to see recommendations' };
      }

      const focuses = (latestInsight.focus_areas?.split('\n') || [])
        .map(f => f.replace(/^[-•*\d.)\]]+\s*/, '').trim())
        .filter(f => f.length > 0);

      return {
        count: focuses.length,
        primaryFocus: focuses[0] || 'No focus areas identified yet'
      };
    };

    const themes = extractThemes();
    const focusAreas = extractCurrentFocusAreas();

    // Get top 5 breakthroughs from most recent sessions
    let topBreakthroughs = [];
    if (clientData.coachingInsights && clientData.coachingInsights.length > 0) {
      // Get insights from newest to oldest
      const allInsights = [...clientData.coachingInsights];

      allInsights.forEach(insight => {
        if (topBreakthroughs.length >= 5) return; // Stop when we have 5

        if (insight.breakthroughs) {
          let breakthroughText = insight.breakthroughs;

          // Try to parse if it's a JSON array
          try {
            if (breakthroughText.startsWith('[')) {
              const parsed = JSON.parse(breakthroughText);
              if (Array.isArray(parsed)) {
                // Take the first breakthrough from each session until we have 5
                parsed.forEach(item => {
                  if (topBreakthroughs.length < 5) {
                    const cleaned = item
                      .replace(/^["'\[]+|["'\]]+$/g, '')
                      .trim();
                    if (cleaned) topBreakthroughs.push(cleaned);
                  }
                });
              }
            } else {
              // If not JSON, take lines
              const lines = (breakthroughText?.split('\n') || []).filter(l => l.trim().length > 0);
              lines.forEach(line => {
                if (topBreakthroughs.length < 5) {
                  const cleaned = line
                    .replace(/^[-•*\d.)\]]+\s*/, '')
                    .replace(/^["'\[]+|["'\]]+$/g, '')
                    .trim();
                  if (cleaned) topBreakthroughs.push(cleaned);
                }
              });
            }
          } catch (e) {
            // If parsing fails, just take lines
            const lines = (breakthroughText?.split('\n') || []).filter(l => l.trim().length > 0);
            lines.forEach(line => {
              if (topBreakthroughs.length < 5) {
                const cleaned = line
                  .replace(/^[-•*\d.)\]]+\s*/, '')
                  .replace(/^["'\[]+|["'\]]+$/g, '')
                  .trim();
                if (cleaned) topBreakthroughs.push(cleaned);
              }
            });
          }
        }
      });
    }

    // Clean up text by removing array brackets and extra quotes
    const cleanText = (text) => {
      if (!text) return text;
      return text
        .replace(/^\["|"\]$/g, '') // Remove leading [" and trailing "]
        .replace(/^["']|["']$/g, '') // Remove leading/trailing quotes
        .trim();
    };

    // Extract top 3 stuck patterns from ALL sessions (not just latest)
    const getTopStuckPatterns = () => {
      if (!clientData.coachingInsights || clientData.coachingInsights.length === 0) return [];

      const allPatterns = [];

      // Collect all stuck patterns from all insights
      clientData.coachingInsights.forEach(insight => {
        if (insight.stuck_patterns) {
          let patterns = insight.stuck_patterns;

          // Try to parse if it's JSON array format
          try {
            if (patterns.startsWith('[')) {
              const parsed = JSON.parse(patterns);
              if (Array.isArray(parsed)) {
                parsed.forEach(p => {
                  const cleaned = p.replace(/^["'\[]+|["'\]]+$/g, '').trim();
                  if (cleaned) allPatterns.push(cleaned);
                });
              }
            } else {
              // Split by newlines
              const lines = (patterns?.split('\n') || []).filter(line => line.trim().length > 0);
              lines.forEach(line => {
                const cleaned = line
                  .replace(/^[-•*\d.)\]]+\s*/, '') // Remove bullet points
                  .replace(/^["'\[]+|["'\]]+$/g, '') // Remove quotes and brackets
                  .trim();
                if (cleaned) allPatterns.push(cleaned);
              });
            }
          } catch (e) {
            // If parsing fails, split by newlines
            const lines = (patterns?.split('\n') || []).filter(line => line.trim().length > 0);
            lines.forEach(line => {
              const cleaned = line
                .replace(/^[-•*\d.)\]]+\s*/, '')
                .replace(/^["'\[]+|["'\]]+$/g, '')
                .trim();
              if (cleaned) allPatterns.push(cleaned);
            });
          }
        }
      });

      if (allPatterns.length === 0) return [];

      // Count pattern frequency with fuzzy matching
      const patternCounts = {};
      const normalizedToOriginal = {};

      allPatterns.forEach(pattern => {
        // Normalize for comparison (lowercase, remove punctuation)
        const normalized = pattern.toLowerCase()
          .replace(/[.,;:!?]/g, '')
          .replace(/\s+/g, ' ')
          .trim();

        if (patternCounts[normalized]) {
          patternCounts[normalized]++;
          // Keep the longer/more detailed version
          if (pattern.length > normalizedToOriginal[normalized].length) {
            normalizedToOriginal[normalized] = pattern;
          }
        } else {
          patternCounts[normalized] = 1;
          normalizedToOriginal[normalized] = pattern;
        }
      });

      // Sort by frequency and take top 3
      const topPatterns = Object.entries(patternCounts)
        .sort((a, b) => b[1] - a[1]) // Sort by count descending
        .slice(0, 3)
        .map(([normalized, count]) => ({
          text: normalizedToOriginal[normalized],
          count: count
        }));

      return topPatterns;
    };

    // Extract top 3 focus areas
    const getTopFocusAreas = () => {
      if (!latestInsight?.focus_areas) return [];

      let focuses = latestInsight.focus_areas;
      const result = [];

      // Try to parse if it's JSON array format
      try {
        if (focuses.startsWith('[')) {
          const parsed = JSON.parse(focuses);
          if (Array.isArray(parsed)) {
            parsed.slice(0, 3).forEach(f => {
              const cleaned = f.replace(/^["'\[]+|["'\]]+$/g, '').trim();
              if (cleaned) result.push(cleaned);
            });
            return result;
          }
        }
      } catch (e) {
        // Not JSON, continue with text processing
      }

      // Split by newlines and take up to 3 non-empty lines
      const lines = (focuses?.split('\n') || []).filter(line => line.trim().length > 0);

      lines.slice(0, 3).forEach(line => {
        const cleaned = line
          .replace(/^[-•*\d.)\]]+\s*/, '') // Remove bullet points
          .replace(/^["'\[]+|["'\]]+$/g, '') // Remove quotes and brackets
          .trim();
        if (cleaned) result.push(cleaned);
      });

      return result;
    };

    setOverview({
      lastSessionDate,
      nextSessionDate,
      daysUntilNext,
      sessionFrequency: frequency,
      totalSessions: clientData.sessionNotes?.length || 0,
      activeObjectives,
      completedObjectives,
      themesCount: themes.count,
      topThemes: themes.topThemes.map(t => cleanText(t)),
      focusAreasCount: focusAreas.count,
      primaryFocus: cleanText(focusAreas.primaryFocus),
      stuckPatterns: getTopStuckPatterns(),
      nextSteps: getTopFocusAreas(),
      topBreakthroughs: topBreakthroughs
    });

    // Set editing state with current values
    if (clientData.client?.next_session_date) {
      setNextSessionDate(clientData.client.next_session_date);
    }
    setSessionFrequency(frequency);
    if (clientData.client?.custom_interval_days) {
      setCustomIntervalDays(clientData.client.custom_interval_days);
    }
  };

  const startEditingSession = () => {
    setEditingSession(true);
  };

  const cancelEditingSession = () => {
    setEditingSession(false);
    // Reset to original values
    if (clientData.client?.next_session_date) {
      setNextSessionDate(clientData.client.next_session_date);
    }
    setSessionFrequency(clientData.client?.session_frequency || 'weekly');
    setCustomIntervalDays(clientData.client?.custom_interval_days || 7);
  };

  const saveSessionSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {};

      // Only include fields that have values
      if (nextSessionDate) {
        payload.next_session_date = nextSessionDate;
      }
      if (sessionFrequency) {
        payload.session_frequency = sessionFrequency;
      }

      // Include custom_interval_days only when frequency is 'custom'
      if (sessionFrequency === 'custom' && customIntervalDays) {
        payload.custom_interval_days = parseInt(customIntervalDays);
      }

      const response = await axios.put(`${API_URL}/clients/${clientId}/session-schedule`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update clientData with the response from the server
      if (clientData.client && response.data) {
        clientData.client.next_session_date = response.data.next_session_date;
        clientData.client.session_frequency = response.data.session_frequency;
        clientData.client.custom_interval_days = response.data.custom_interval_days;
        generateOverview();
      }

      toast.success('Session schedule updated!');
      setEditingSession(false);
    } catch (error) {
      console.error('Error saving session schedule:', error);
      toast.error(error.response?.data?.error || 'Failed to update session schedule');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[var(--border-color)] border-t-[var(--accent)]"></div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--text-secondary)]">Loading overview...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
          {clientName}'s Overview
        </h1>
        <p className="text-[var(--text-secondary)]">
          Executive summary of progress, momentum, and key focus areas
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Session Activity */}
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-5" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-[var(--accent)]" />
            <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
              Most Recent Session
            </h3>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)] mb-1">{overview.lastSessionDate}</p>
          <p className="text-sm text-[var(--text-secondary)]">
            Last session date
          </p>
          <p className="text-xs text-[var(--text-tertiary)] mt-2">
            {overview.totalSessions} {overview.totalSessions === 1 ? 'session' : 'sessions'} total
          </p>
        </div>

        {/* Next Session */}
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-5" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[var(--accent)]" />
              <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                Next Session
              </h3>
            </div>
            {!editingSession ? (
              <button
                onClick={startEditingSession}
                className="p-1 hover:bg-[var(--hover-bg)] rounded transition-colors"
                title="Edit session schedule"
              >
                <Edit2 className="w-4 h-4 text-[var(--text-tertiary)]" />
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={saveSessionSchedule}
                  className="p-1 hover:bg-[var(--hover-bg)] rounded transition-colors"
                  title="Save"
                >
                  <Save className="w-4 h-4 text-green-500" />
                </button>
                <button
                  onClick={cancelEditingSession}
                  className="p-1 hover:bg-[var(--hover-bg)] rounded transition-colors"
                  title="Cancel"
                >
                  <X className="w-4 h-4 text-red-500" />
                </button>
              </div>
            )}
          </div>

          {!editingSession ? (
            <>
              <p className="text-2xl font-bold text-[var(--text-primary)] mb-1">{overview.nextSessionDate}</p>
              <p className="text-sm text-[var(--text-secondary)]">
                {overview.daysUntilNext !== null ? (
                  overview.daysUntilNext === 0 ? 'Today' :
                  overview.daysUntilNext === 1 ? 'Tomorrow' :
                  overview.daysUntilNext > 0 ? `In ${overview.daysUntilNext} days` :
                  `${Math.abs(overview.daysUntilNext)} days ago`
                ) : 'Scheduled date'}
              </p>
              <p className="text-xs text-[var(--text-tertiary)] mt-2">
                {overview.sessionFrequency === 'biweekly' ? 'Biweekly sessions' :
                 overview.sessionFrequency === 'monthly' ? 'Monthly sessions' :
                 overview.sessionFrequency === 'custom' ?
                   (clientData.client?.custom_interval_days ?
                     `Every ${clientData.client.custom_interval_days} days` :
                     'Custom frequency') :
                 'Weekly sessions'}
              </p>
            </>
          ) : (
            <div className="space-y-3 mt-2">
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">Session Date</label>
                <div className="relative flex gap-2">
                  <input
                    ref={dateInputRef}
                    type="date"
                    value={nextSessionDate}
                    onChange={(e) => setNextSessionDate(e.target.value)}
                    className="flex-1 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] text-sm focus:outline-none focus:border-[var(--accent)]"
                  />
                  <button
                    type="button"
                    onClick={() => dateInputRef.current?.showPicker()}
                    className="px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded hover:bg-[var(--hover-bg)] transition-colors"
                    title="Open calendar"
                  >
                    <CalendarDays className="w-4 h-4 text-[var(--text-secondary)]" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">Frequency</label>
                <select
                  value={sessionFrequency}
                  onChange={(e) => setSessionFrequency(e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] text-sm focus:outline-none focus:border-[var(--accent)]"
                >
                  <option value="weekly">Weekly (7 days)</option>
                  <option value="biweekly">Biweekly (14 days)</option>
                  <option value="monthly">Monthly (30 days)</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {sessionFrequency === 'custom' && (
                <div>
                  <label className="block text-xs text-[var(--text-secondary)] mb-1">Custom Interval (days)</label>
                  <input
                    type="number"
                    min="1"
                    value={customIntervalDays}
                    onChange={(e) => setCustomIntervalDays(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] text-sm focus:outline-none focus:border-[var(--accent)]"
                    placeholder="Enter days (e.g., 10, 21)"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Progress Journey */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-5 h-5 text-[var(--accent)]" />
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Progress Journey</h2>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">Top 5 most recent breakthroughs</p>
          </div>
        </div>
        {overview.topBreakthroughs && overview.topBreakthroughs.length > 0 ? (
          <ul className="space-y-3">
            {overview.topBreakthroughs.map((breakthrough, idx) => (
              <li key={idx} className="flex gap-3">
                <span className="text-[var(--accent)] mt-1 flex-shrink-0 font-semibold">{idx + 1}.</span>
                <span className="text-[var(--text-primary)] leading-relaxed">
                  {breakthrough}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[var(--text-tertiary)] italic">
            No breakthroughs recorded yet.
          </p>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Primary Challenges */}
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-orange-400" />
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Primary Challenges</h2>
              <p className="text-xs text-[var(--text-tertiary)] mt-0.5">Most frequent patterns across all sessions</p>
            </div>
          </div>
          {overview.stuckPatterns && overview.stuckPatterns.length > 0 ? (
            <ul className="space-y-3">
              {overview.stuckPatterns.map((pattern, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="text-orange-400 mt-1 flex-shrink-0 font-semibold">{idx + 1}.</span>
                  <div className="flex-1">
                    <span className="text-[var(--text-primary)] leading-relaxed">
                      {pattern.text}
                    </span>
                    {pattern.count > 1 && (
                      <span className="ml-2 text-xs text-[var(--text-tertiary)] italic">
                        (appeared in {pattern.count} {pattern.count === 1 ? 'session' : 'sessions'})
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[var(--text-tertiary)] italic">
              Gathering data...
            </p>
          )}
        </div>

        {/* Focus Areas */}
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-5 h-5 text-[var(--accent)]" />
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Focus Areas</h2>
              <p className="text-xs text-[var(--text-tertiary)] mt-0.5">Key areas to explore in upcoming sessions</p>
            </div>
          </div>
          {overview.nextSteps && overview.nextSteps.length > 0 ? (
            <ul className="space-y-3">
              {overview.nextSteps.map((step, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="text-[var(--accent)] mt-1 flex-shrink-0 font-semibold">{idx + 1}.</span>
                  <span className="text-[var(--text-primary)] leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[var(--text-tertiary)] italic">
              Upload a session to see recommendations
            </p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg p-5">
        <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => onNavigate('sessionPlan')}
            className="px-4 py-2 bg-[var(--accent)] text-white rounded-lg font-medium transition-colors hover:opacity-90"
          >
            Plan Next Session
          </button>
          <button
            onClick={() => onNavigate('notes')}
            className="px-4 py-2 rounded-lg font-medium transition-colors border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }}
          >
            View Session Notes
          </button>
          <button
            onClick={() => onNavigate('breakthroughs')}
            className="px-4 py-2 rounded-lg font-medium transition-colors border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }}
          >
            See Insights
          </button>
          <button
            onClick={() => onNavigate('trends')}
            className="px-4 py-2 rounded-lg font-medium transition-colors border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }}
          >
            See Trends
          </button>
        </div>
      </div>
    </div>
  );
}

export default ClientOverview;
